import { useEffect, useMemo, useRef, useState } from "react";

import bankidSvg from "@bokio/assets/images/bankid.svg";
import { launchBankIdApp } from "@bokio/components/BankIdSecureStart/BankIdSecureStart";
import { Button } from "@bokio/designsystem/components/Button";
import { LoadingSpinner } from "@bokio/designsystem/components/Loading";
import { Notice } from "@bokio/designsystem/components/Notice/Notice";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { Caption, Paragraph, TextSpan } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { BokioBusinessAccountIcon } from "@bokio/elements/BokioBusinessAccountIcon/BokioBusinessAccountIcon";
import { Link } from "@bokio/elements/Link/Link";
import { BankLangFactory, GeneralLangFactory } from "@bokio/lang";
import { formatMessage } from "@bokio/shared/utils/format";

import { SveaBankIdModalDialogBase } from "./SveaBankIdModalDialogBase";
import { SveaBankIdQrCodeButton } from "./SveaBankIdQrCodeButton";

import type { NewSveaBankIdSignFlowResult } from "@bokio/mobile-web-shared/areas/bank/hooks/useNewSveaBankIdSignFlow";
import type { FC } from "react";

import * as styles from "./sveaBankIdSignModalDialog.scss";

const QR_COUNTDOWN_SECONDS = 30;

export const SveaBankIdSignModalDialog: FC<NewSveaBankIdSignFlowResult> = ({
	signState,
	cancel,
	bankIdToken,
	debugInfo,
}) => {
	const bankLang = BankLangFactory();
	const generalLang = GeneralLangFactory();
	const [showHowToScan, setShowHowToScan] = useState(false);
	const [sessionExpiringAt, setSessionExpiringAt] = useState<Date | null>(null);
	const [now, setNow] = useState(() => new Date());
	const prevSignStateType = useRef<string | null>(null);

	const secondsLeft = useMemo(() => {
		if (!sessionExpiringAt) {
			return 0;
		}
		return Math.max(0, Math.round((sessionExpiringAt.getTime() - now.getTime()) / 1000));
	}, [sessionExpiringAt, now]);

	useEffect(() => {
		const interval = setInterval(() => setNow(new Date()), 1000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		if (signState.type === "PendingStart" && prevSignStateType.current !== "PendingStart") {
			const expiresAt = new Date();
			expiresAt.setSeconds(expiresAt.getSeconds() + QR_COUNTDOWN_SECONDS);
			setSessionExpiringAt(expiresAt);
		}
		prevSignStateType.current = signState.type;
	}, [signState.type]);

	const qrCode = signState.type === "PendingStart" ? signState.qrCode : undefined;

	const renderBody = () => {
		switch (signState.type) {
			case "Initializing":
			case "Cancelling":
			case "Polling":
			case "Successful":
				return (
					<div className={styles.cardBody}>
						<LoadingSpinner />
					</div>
				);

			case "PendingStart":
				return (
					<div className={styles.cardBody}>
						<Paragraph>{bankLang.SveaBankIdSign_ScanInstruction}</Paragraph>
						<SveaBankIdQrCodeButton
							qrCode={qrCode}
							ariaLabel={bankLang.SveaBankId_QrCodeAriaLabel}
							fullscreenAriaLabel={bankLang.SveaBankId_QrFullscreenAriaLabel}
							fullscreenImageAlt={bankLang.SveaBankId_QrFullscreenImageAlt}
							timerText={formatMessage(bankLang.SveaBankId_QrSecondsLeft, secondsLeft)}
						/>
						<div className={styles.howToScanSection}>
							<button className={styles.howToScanToggle} onClick={() => setShowHowToScan(v => !v)}>
								<svg
									width="10"
									height="6"
									viewBox="0 0 9.33333 5.33333"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									style={{ transform: showHowToScan ? "rotate(0deg)" : "rotate(180deg)" }}
									aria-hidden="true"
								>
									<path
										d="M4.19526 0.195262C4.45561 -0.0650874 4.87772 -0.0650874 5.13807 0.195262L9.13807 4.19526C9.39842 4.45561 9.39842 4.87772 9.13807 5.13807C8.87772 5.39842 8.45561 5.39842 8.19526 5.13807L4.66667 1.60948L1.13807 5.13807C0.877722 5.39842 0.455612 5.39842 0.195262 5.13807C-0.0650874 4.87772 -0.0650874 4.45561 0.195262 4.19526L4.19526 0.195262Z"
										fill="currentColor"
									/>
								</svg>
								<Caption>{bankLang.SveaBankId_HowToScan}</Caption>
							</button>
							{showHowToScan && (
								<ol className={styles.howToScanSteps}>
									<li>
										<Paragraph>{bankLang.SveaBankId_HowToScan_Step1}</Paragraph>
									</li>
									<li>
										<Paragraph>
											{bankLang.SveaBankId_HowToScan_Step2_Prefix}
											<TextSpan style="body-bold">{bankLang.SveaBankId_HowToScan_Step2_Bold}</TextSpan>
											{bankLang.SveaBankId_HowToScan_Step2_Suffix}
										</Paragraph>
									</li>
									<li>
										<Paragraph>{bankLang.SveaBankId_HowToScan_Step3}</Paragraph>
									</li>
									<li>
										<Paragraph>{bankLang.SveaBankId_HowToScan_Step4}</Paragraph>
									</li>
								</ol>
							)}
						</div>
					</div>
				);

			case "AwaitingAuthorization":
				return (
					<div className={styles.cardBody}>
						<Paragraph>{bankLang.SveaBankIdSign_CompleteInApp}</Paragraph>
						<Button appearance="secondary" onClick={cancel}>
							{bankLang.SveaBankId_Cancel}
						</Button>
					</div>
				);

			case "Completed":
				return null;

			case "Cancelled":
				return (
					<div className={styles.cardBody}>
						<Paragraph>{generalLang.BankId_UserMessage_RFA6}</Paragraph>
					</div>
				);

			case "Failed":
				return (
					<div className={styles.cardBody}>
						{signState.reason === "StartFailed" ? (
							<Paragraph>{bankLang.SveaBankId_QrExpired}</Paragraph>
						) : signState.reason === "UserCancelled" ? (
							<Paragraph>{generalLang.BankId_UserMessage_RFA3}</Paragraph>
						) : (
							<Notice color="error" hasIcon collapsible={false}>
								{bankLang.SveaBankIdSign_Failed_Description_Prefix}
								<Link external="mailto:support@bokio.se">{bankLang.SveaBankId_Failed_SupportLink}</Link>
								{bankLang.SveaBankId_Failed_Description_Suffix}
							</Notice>
						)}
					</div>
				);

			case "Error":
				return (
					<div className={styles.cardBody}>
						<Notice color="error" hasIcon collapsible={false}>
							{bankLang.SveaBankIdSign_Failed_Description_Prefix}
							<Link external="mailto:support@bokio.se">{bankLang.SveaBankId_Failed_SupportLink}</Link>
							{bankLang.SveaBankId_Failed_Description_Suffix}
						</Notice>
					</div>
				);
		}
	};

	return (
		<SveaBankIdModalDialogBase
			title={bankLang.SveaBankIdSign_Title}
			onClose={cancel}
			header={
				<>
					<span className={styles.headerLabel}>
						<Caption>{bankLang.SveaBankIdSign_ConfirmBy}</Caption>
					</span>
					<div className={styles.productNameRow}>
						<TextSpan style="large-heading-bold">{bankLang.SveaBankIdSign_Title}</TextSpan>
						<BokioBusinessAccountIcon size="24" />
					</div>
				</>
			}
			body={renderBody()}
			footerExtra={
				signState.type === "PendingStart" && bankIdToken ? (
					<SG align="center">
						<Button
							appearance="secondary"
							icon={<img src={bankidSvg} height="20" alt="" />}
							onClick={() => launchBankIdApp(bankIdToken)}
						>
							{generalLang.BankID_OpenOnThisDevice}
						</Button>
					</SG>
				) : undefined
			}
			debugInfo={debugInfo}
		/>
	);
};
